import { Select } from "./select";
import { cn } from "@hey404/ui";
import { inputClass } from "./admin-ui";
import { useState, type ReactNode } from "react";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { splitBlanks, type Answer, type PublicQuestion } from "@hey404/quiz";

/**
 * One answer option as a full-width row you click anywhere on: a letter (or
 * icon) badge, the text, and a clear selected state (acid fill, ink badge).
 * The real input stays for keyboard and screen readers, visually hidden.
 */
function OptionRow({
  type,
  name,
  checked,
  onChange,
  badge,
  children,
}: {
  type: "radio" | "checkbox";
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  badge: ReactNode;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        "border-chip rounded-control flex cursor-pointer items-center gap-3 px-3 py-2.5 text-[15px] leading-snug transition-colors has-[:focus-visible]:shadow-[0_0_0_3px_var(--focus-ring)]",
        checked
          ? "border-ink bg-acid/40"
          : "border-ink/30 bg-paper hover:border-ink hover:bg-paper-dim",
      )}
    >
      <input
        type={type}
        name={name}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          "border-ink grid h-7 w-7 shrink-0 place-items-center border-[1.5px] font-mono text-xs font-bold transition-colors",
          type === "radio" ? "rounded-full" : "rounded-[4px]",
          checked ? "bg-ink text-paper" : "bg-paper",
        )}
      >
        {checked && type === "checkbox" ? (
          <Check size={14} strokeWidth={3} />
        ) : (
          badge
        )}
      </span>
      <span className={cn("min-w-0 flex-1", checked && "person-name")}>
        {children}
      </span>
    </label>
  );
}

/** Whether a question has an answer complete enough to send. */
export function isAnswered(question: PublicQuestion, answer?: Answer) {
  const content = question.content;
  switch (content.type) {
    case "multiple_choice":
      return answer?.type === "multiple_choice" && answer.selected.length > 0;
    case "true_false":
      return answer?.type === "true_false";
    case "written":
      return answer?.type === "written" && answer.text.trim().length > 0;
    case "sort":
      return true;
    case "match":
      return (
        answer?.type === "match" &&
        content.left.every((item) => Boolean(answer.pairs[item.id]))
      );
    case "fill_in_the_blank":
      return (
        answer?.type === "fill_in_the_blank" &&
        content.blanks.every(
          (_, index) => typeof answer.selected[index] === "number",
        )
      );
  }
}

/**
 * Learner-facing inputs, one per question type.
 *
 * Each renderer owns its slice of the answer map and reports a fully formed
 * `Answer`, so the submitting form never has to know how a type is shaped.
 * The payloads that reach the API are exactly what `grade` expects.
 */
export function QuestionInput({
  question,
  answer,
  onChange,
}: {
  question: PublicQuestion;
  answer: Answer | undefined;
  onChange: (answer: Answer) => void;
}) {
  const content = question.content;

  switch (content.type) {
    case "multiple_choice": {
      const selected =
        answer?.type === "multiple_choice" ? answer.selected : [];
      return (
        <div className="space-y-2">
          {content.multiple && (
            <p className="text-ink-soft text-xs">
              Puedes marcar más de una opción.
            </p>
          )}
          {content.options.map((option, index) => (
            <OptionRow
              key={index}
              type={content.multiple ? "checkbox" : "radio"}
              name={question.id}
              checked={selected.includes(index)}
              badge={String.fromCharCode(65 + index)}
              onChange={(checked) =>
                onChange({
                  type: "multiple_choice",
                  selected: content.multiple
                    ? checked
                      ? [...selected, index]
                      : selected.filter((value) => value !== index)
                    : [index],
                })
              }
            >
              {option}
            </OptionRow>
          ))}
        </div>
      );
    }

    case "true_false": {
      const value = answer?.type === "true_false" ? answer.value : null;
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          {[true, false].map((option) => (
            <OptionRow
              key={String(option)}
              type="radio"
              name={question.id}
              checked={value === option}
              badge={
                option ? (
                  <Check size={14} strokeWidth={3} />
                ) : (
                  <X size={14} strokeWidth={3} />
                )
              }
              onChange={() => onChange({ type: "true_false", value: option })}
            >
              {option ? "Verdadero" : "Falso"}
            </OptionRow>
          ))}
        </div>
      );
    }

    case "written":
      return (
        <textarea
          rows={3}
          value={answer?.type === "written" ? answer.text : ""}
          onChange={(event) =>
            onChange({ type: "written", text: event.target.value })
          }
          aria-label="Tu respuesta"
          placeholder="Escribe tu respuesta con tus palabras"
          className={cn(inputClass, "mt-0 resize-y px-3 py-2.5 text-[15px]")}
        />
      );

    case "sort": {
      // The server already shuffled these, so the stored order is the
      // learner's working order until they move something.
      const order =
        answer?.type === "sort"
          ? answer.order
          : content.options.map((option) => option.id);
      const byId = new Map(content.options.map((o) => [o.id, o.text]));
      const move = (index: number, direction: -1 | 1) => {
        const next = [...order];
        const target = next[index + direction];
        const current = next[index];
        if (!target || !current) return;
        next[index + direction] = current;
        next[index] = target;
        onChange({ type: "sort", order: next });
      };
      return (
        <ol className="space-y-2">
          {order.map((id, index) => (
            <li
              key={id}
              className="border-ink/30 bg-paper border-chip rounded-control flex items-center gap-3 py-1.5 pl-3 pr-1.5"
            >
              <span className="border-ink bg-lemon grid h-7 w-7 shrink-0 place-items-center rounded-full border-[1.5px] font-mono text-xs font-bold">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 text-[15px] leading-snug">
                {byId.get(id)}
              </span>
              {(
                [
                  [-1, "Subir", ChevronUp],
                  [1, "Bajar", ChevronDown],
                ] as const
              ).map(([direction, label, Icon]) => (
                <button
                  key={label}
                  type="button"
                  aria-label={`${label} «${byId.get(id)}»`}
                  disabled={
                    direction === -1 ? index === 0 : index === order.length - 1
                  }
                  onClick={() => move(index, direction)}
                  className="hover:bg-paper-dim rounded-control grid h-9 w-9 cursor-pointer place-items-center disabled:cursor-not-allowed disabled:opacity-25"
                >
                  <Icon size={16} aria-hidden="true" />
                </button>
              ))}
            </li>
          ))}
        </ol>
      );
    }

    case "match": {
      const pairs = answer?.type === "match" ? answer.pairs : {};
      return (
        <div className="space-y-2">
          {content.left.map((item) => (
            <div
              key={item.id}
              className="border-ink/30 bg-paper border-chip rounded-control flex flex-wrap items-center gap-2 py-1.5 pl-3 pr-1.5"
            >
              <span className="min-w-0 flex-1 text-[15px]">{item.text}</span>
              <Select
                wrapperClassName="w-auto min-w-40"
                value={pairs[item.id] ?? ""}
                onChange={(event) =>
                  onChange({
                    type: "match",
                    pairs: { ...pairs, [item.id]: event.target.value },
                  })
                }
                className={cn(inputClass, "mt-0 px-2 py-1 text-sm")}
              >
                <option value="">Elige…</option>
                {content.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>
      );
    }

    case "fill_in_the_blank": {
      const selected =
        answer?.type === "fill_in_the_blank" ? answer.selected : [];
      return (
        <p className="text-[15px] leading-9">
          {splitBlanks(content.text).map((part, index) =>
            part.kind === "text" ? (
              <span key={index}>{part.text}</span>
            ) : (
              <Select
                key={index}
                wrapperClassName="mx-1 inline-flex w-auto align-middle"
                value={selected[part.index] ?? ""}
                onChange={(event) => {
                  const next = [...selected];
                  next[part.index] = Number(event.target.value);
                  onChange({ type: "fill_in_the_blank", selected: next });
                }}
                className={cn(inputClass, "mt-0 min-h-9 px-2 py-1 text-sm")}
              >
                <option value="">…</option>
                {(content.blanks[part.index]?.options ?? []).map(
                  (option, optionIndex) => (
                    <option key={optionIndex} value={optionIndex}>
                      {option}
                    </option>
                  ),
                )}
              </Select>
            ),
          )}
        </p>
      );
    }
  }
}

/** Answer state for a whole quiz, keyed by question id. */
export function useQuizAnswers() {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const set = (questionId: string, answer: Answer) =>
    setAnswers((current) => ({ ...current, [questionId]: answer }));
  return { answers, set, reset: () => setAnswers({}) };
}
